import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Modal from "react-modal";
import Core from "../../common/clientSdk";
import Utils from "../../common/utils";
import { InfluencerAnalyticsShareLink } from "../../modal/influencerAnalyticsShare";
import Icon from "../../components/core_component/icon";
import { InstagramEmbed, YouTubeEmbed, XEmbed } from "react-social-media-embed";
import { PrimaryCta } from "../../components";

// Import components
import {
    AnalyticsHeader,
    AnalyticsTabs,
    OverviewSection,
    TimelineSection,
    TopPerformersSection,
    InfluencersSection,
    ContentSection,
    GenerateReportFallback,
    ReportInProgressFallback,
    NoContractsFallback,
    RefreshDataBanner,
    ManageSectionSidebar,
    CustomizeMetricsSidebar,
} from "./components";

import AnalyticsHeaderTop from "./components/AnalyticsHeaderTop";

import "./analyticsCampaign.css";

function AnalyticsCampaign(props) {
    const [isLoading, setLoading] = useState(true); 
    const [campaignReport, setCampaignReport] = useState(null);
    const [contentList, setContentList] = useState([]);
    const [campaignPlatform, setCampaignPlatform] = useState(null);
    const [campaignAnalytic, setCampaignAnalytic] = useState("");
    const [campaignContentAnalytic, setCampaignContentAnalytic] = useState(null);
    const [assignmentInfluencerList, setAssignmentInfluencerList] = useState([]);
    const [timelineData, setTimelineData] = useState([]);
    const [reportType, setReportType] = useState("user");
    const [influencerCount, setInfluencerCount] = useState(0);
    const [contentCount, setContentCount] = useState(0);
    const [hasAssignments, setHasAssignments] = useState(null); // null=loading, true/false
    const [isReportGenerated, setIsReportGenerated] = useState(false);
    const [lastGeneratedDate, setLastGeneratedDate] = useState(null);
    const [refreshDisabled, setRefreshDisabled] = useState(false);
    const [refreshTimeLeft, setRefreshTimeLeft] = useState(0);

    const [pdfLoader, setPDFLoader] = useState(false);
    const [isPdfView, setIsPdfView] = useState(false);
    const [reportStatus, setReportStatus] = useState(null);
    const [generateReportLoading, setGenerateReportLoading] = useState(false);
    const [, setShowFallbackReport] = useState(false);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);

    /** Tracks parallel APIs kicked off from loadCampaignData so section shimmers stay until all respond */
    const inInitialLoadBatchRef = useRef(false);
    const initialApisRemaining = useRef(0);

    const bumpInitialLoadDone = useCallback(() => {
        if (!inInitialLoadBatchRef.current) return;
        initialApisRemaining.current -= 1;
        if (initialApisRemaining.current <= 0) {
            inInitialLoadBatchRef.current = false;
            setLoading(false);
        }
    }, []);

    const startInitialDataBatch = useCallback((reportExists) => {
        inInitialLoadBatchRef.current = true;
        initialApisRemaining.current = reportExists ? 6 : 3;
        setLoading(true);
    }, []);
    const [modalIsOpenShare, setModalIsOpenShare] = useState(false);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [currentReportId, setCurrentReportId] = useState(null);

    // Manage Section Sidebar state
    const [manageSectionSidebarOpen, setManageSectionSidebarOpen] = useState(false);
    const [manageSectionSaving, setManageSectionSaving] = useState(false);
    const [customizeMetricsSidebarOpen, setCustomizeMetricsSidebarOpen] = useState(false);
    const [customMetricValues, setCustomMetricValues] = useState({});
    const [sectionVisibility, setSectionVisibility] = useState(
        // Initialize from props if available, otherwise defaults
        props.campaignDetail?.campaign_data?.visibility || {
            overview: true,
            timeline: true,
            topPerformers: true,
            influencers: true,
            content: true,
        }
    );

    // Sync visibility with props when they update (e.g. after API fetch)
    useEffect(() => {
        if (props.campaignDetail?.campaign_data?.visibility) {
            setSectionVisibility(props.campaignDetail.campaign_data.visibility);
        }
    }, [props.campaignDetail?.campaign_data?.visibility]);

    // UI state
    const [activeTab, setActiveTab] = useState("Overview");
    const visibleSections = useMemo(() => {
        const allTabs = ["Overview", "Timeline", "Top Performers", "Influencers", "Content"];
        const campaignType = props.campaignDetail?.campaign_data?.type;

        return allTabs.filter((tab) => {
            if (campaignType === "content" && (tab === "Overview" || tab === "Top Performers")) {
                return false;
            }

            const key = tab === "Top Performers" ? "topPerformers" : tab.toLowerCase();

            if (campaignType !== "content" && key === "overview") {
                return true;
            }

            return sectionVisibility?.[key] !== false;
        });
    }, [props.campaignDetail?.campaign_data?.type, sectionVisibility]);
    const sectionRefs = useRef({});
    const pdfRef = useRef();
    const isManualScrolling = useRef(false);

    const adminUser = useSelector((state) => state?.AppDataReducer) || {};

    const brandIdResolved =
        props.brandId ?? props.campaignDetail?.brand_data?.brand_id;
    const orgIdResolved =
        props.orgId ?? props.campaignDetail?.brand_data?.org_id;

    const brand_deep_data = {
        brand_data: props.campaignDetail?.brand_data || {},
        brand_id: brandIdResolved,
    };

    const org_deep_data = {
        org_id: orgIdResolved,
        org_data: {
            organization_type:
                props.campaignDetail?.brand_data?.organization_type ||
                props.campaignDetail?.org_data?.organization_type,
        },
    };

    const user_data = {
        country:
            props.campaignDetail?.brand_data?.country ||
            adminUser?.country ||
            "IN",
        ...adminUser,
    };

    const sanitizeMetrics = useCallback((data) => {
        if (data === null || data === undefined) return data;
        if (Array.isArray(data)) {
            return data.map((item) => sanitizeMetrics(item));
        }
        if (typeof data === "object") {
            const sanitized = { ...data };
            Object.keys(sanitized).forEach((key) => {
                const value = sanitized[key];
                if (
                    typeof value === "number" ||
                    (typeof value === "string" &&
                        value.length > 0 &&
                        !isNaN(Number(value)) &&
                        /^-?\d+(\.\d+)?$/.test(value))
                ) {
                    const num = Number(value);
                    if (num <= 0) {
                        sanitized[key] = 0;
                    }
                } else if (typeof value === "object" && value !== null) {
                    sanitized[key] = sanitizeMetrics(value);
                }
            });
            return sanitized;
        }
        return data;
    }, []);

    // Initialization Logic
    useEffect(() => {
        checkAssignments();
    }, []);

    const checkAssignments = () => {
        setLoading(true);
        const params = { campaign_id: props.campaignId };

        // Using getAssignmentUser to check for ANY assignments
        Core.getAssignmentUser(
            (response) => {
                // Correctly access assignment_list based on AssignmentListScreen and clientSdk
                // API returns { success: true, assignment_list: [...] }
                const assignments = response?.assignment_list || [];
                console.log("INITIALIZATION: Assignments list:", assignments);


                // Also check completed count if needed, but request said "if there are any completed assignments".
                // Usually "assignments" endpoint returns all. We can check status if strictly needed.
                // Assuming existence is enough to proceed to report check, or perform a filter.
                const completed = assignments.filter(a => a.assignment_data?.assignment_status === 'COMPLETED');

                console.log("INITIALIZATION: Completed assignments count:", completed.length);

                // If NO completed assignments -> Show Fallback
                if (completed.length === 0) {
                    setHasAssignments(false);
                    setLoading(false);
                } else {
                    setHasAssignments(true);
                    // If Yes -> Check Report Status
                    checkReportStatus();
                }
            },
            () => {
                setHasAssignments(false);
                setLoading(false);
            },
            () => { }, // No progress callback here to avoid flickering
            params
        );
    };



    const loadCampaignData = (reportExists) => {
        startInitialDataBatch(reportExists);

        // Always load influencer list for all campaign types
        getAssignmentInfluencerList();

        AssignmentContentList();
        getCampaignAssignmentProgress();

        // Step 5: Only fetch analytics if report status is COMPLETED (included in batch count)
        if (reportExists) {
            getCampaignReport();
            campaignAnalytics();
            campaignContentAnalytics();
        }
    };

    // Timer Logic for Refresh Button
    useEffect(() => {
        if (!lastGeneratedDate) return;

        const checkTimer = () => {
            const now = Math.floor(Date.now() / 1000);
            const diff = now - lastGeneratedDate;
            const cooldown = 3600; // 60 mins

            if (diff < cooldown) {
                setRefreshDisabled(true);
                setRefreshTimeLeft(Math.ceil((cooldown - diff) / 60)); // Mins left
            } else {
                setRefreshDisabled(false);
                setRefreshTimeLeft(0);
            }
        };

        checkTimer();
        const interval = setInterval(checkTimer, 60000); // Check every min
        return () => clearInterval(interval);
    }, [lastGeneratedDate]);


    // Precise Scroll Spy Logic
    useEffect(() => {
        const handleScroll = () => {
            if (isManualScrolling.current || visibleSections.length === 0) return;

            const stickyLine = getStickyOffset();
            let currentSection = visibleSections[0];

            for (const sectionName of visibleSections) {
                const element = sectionRefs.current[sectionName];
                if (!element) continue;

                const rect = element.getBoundingClientRect();
                if (rect.top <= stickyLine) {
                    currentSection = sectionName;
                }
            }

            if (currentSection !== activeTab) {
                setActiveTab(currentSection);
            }
        };

        let ticking = false;
        const onScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener("scroll", onScroll);
        handleScroll();

        return () => window.removeEventListener("scroll", onScroll);
    }, [activeTab, visibleSections]);


    const isReportInProgress =
        isGeneratingReport ||
        generateReportLoading ||
        reportStatus?.report_status === "IN_PROGRESS";

    // Polling only if in progress
    useEffect(() => {
        if (!isReportInProgress) return;
        const intervalId = setInterval(() => {
            checkReportStatus(true); // Use our new check function with isPolling=true
        }, 10000);
        return () => clearInterval(intervalId);
    }, [isReportInProgress, props.campaignId]);

    useEffect(() => {
        if (reportStatus?.report_status === "IN_PROGRESS") {
            setIsGeneratingReport(true);
            setShowFallbackReport(true);
        }
        if (reportStatus?.report_status === "COMPLETED") {
            setIsGeneratingReport(false);
            setGenerateReportLoading(false);
            setShowFallbackReport(false);
            setIsReportGenerated(true);
        }
    }, [reportStatus?.report_status]);

    // API Functions
    function updateProgress(status) {
        if (inInitialLoadBatchRef.current) return;
        setLoading(status);
    }

    const getContentSyncedList = useCallback(() => {
        let params = { campaign_id: props.campaignId };
        Core.getContentSyncedList(
            getContentSyncedListSuccess,
            getContentSyncedListFailed,
            () => { },
            params
        );
    }, [props.campaignId]);

    function getContentSyncedListSuccess(res, response) {
        const syncedData = response.response || [];
        setAssignmentInfluencerList(syncedData);
    }

    function getContentSyncedListFailed() { }

    const checkReportStatus = (isPolling = false) => {
        const params = { campaign_id: props.campaignId };
        Core.campaignReportStatus(
            (res, response) => {
                const statusData = response?.response;
                setReportStatus(statusData);

                if (statusData?.report_generated_at) {
                    setLastGeneratedDate(statusData.report_generated_at);
                }

                if (statusData?.report_status === "IN_PROGRESS") {
                    setIsGeneratingReport(true);
                    setGenerateReportLoading(true);
                    // Load data on initial check even if in progress, but skip during polling
                    if (!isPolling) {
                        loadCampaignData(true);
                    }
                } else if (statusData?.report_status === "COMPLETED") {
                    // Report just completed - update all states and reload data
                    setIsGeneratingReport(false);
                    setGenerateReportLoading(false);
                    setShowFallbackReport(false);
                    setIsReportGenerated(true);
                    loadCampaignData(true);
                } else if (!statusData || !statusData.report_generated_at) {
                    // Report NOT generated or null
                    setIsReportGenerated(false);
                    // Load data anyway to show "Report not generated" UI with valid structure
                    loadCampaignData(false);
                } else {
                    // Report already generated (legacy case without status field)
                    setIsReportGenerated(true);
                    loadCampaignData(true);
                }
                // Polling: no new batch — clear page loading without touching an active batch
                if (statusData?.report_status === "IN_PROGRESS" && isPolling) {
                    setLoading(false);
                }
            },
            () => {
                // If failed, assume not generated
                setIsReportGenerated(false);
                loadCampaignData(false);
            },
            () => { },
            params
        );
    };

    function campaignReportStatusSuccess(type, res, response) {
        if (response?.response) {
            setReportStatus(response?.response);
        }
        if (response?.response?.report_status === "IN_PROGRESS") {
            // Keep full UI, just show loading state on button
            setGenerateReportLoading(true);
            setIsGeneratingReport(true);
        }
        if (response?.response?.report_status === null) {
            setLoading(false);
            setGenerateReportLoading(false);
            AssignmentContentList();
        }
        if (response?.response?.report_status === "COMPLETED") {
            setIsGeneratingReport(false);
            setGenerateReportLoading(false);
            setShowFallbackReport(false);
            // Reload influencer data for all campaign types
            getAssignmentInfluencerList();
            AssignmentContentList();
            getCampaignReport();
            campaignAnalytics();
            campaignContentAnalytics();
            // Refresh campaign assignment progress after report completes
            getCampaignAssignmentProgress();
        }
    }

    function generateCampaignReportStatusSuccess() {
        getAssignmentContentReport();
    }

    function campaignReportStatusFailed() { }

    function getAssignmentContentReport() {
        let params = { campaign_id: props.campaignId };
        Core.getAssignmentContentReport(
            getAssignmentContentReportSuccess,
            getAssignmentContentReportFailed,
            (status) => setGenerateReportLoading(status),
            params
        );
    }

    function getAssignmentContentReportSuccess() {
        setReportStatus((prev) => ({
            ...prev,
            report_status: "IN_PROGRESS",
        }));
        setIsGeneratingReport(true);
        setGenerateReportLoading(false);
        setShowFallbackReport(true);
    }

    function getAssignmentContentReportFailed() {
        setGenerateReportLoading(false);
    }


    const getAssignmentInfluencerList = useCallback(() => {
        var params = {
            campaign_id: props.campaignId,
        };

        // For UGC campaigns (content type), use /assignment/user/list endpoint
        if (props.campaignDetail?.campaign_data?.type === 'content') {
            Core.getAssignmentUser(
                (response) => {
                    // getAssignmentUser returns { success: true, assignment_list: [...] }
                    const influencerData = sanitizeMetrics(response?.assignment_list || []);
                    setAssignmentInfluencerList(influencerData);
                },
                getAssignmentInfluencerListFailed,
                updateProgress,
                params
            );
        } else {
            // For social campaigns, use the original endpoint
            Core.getAssignmentInfluencerList(
                getAssignmentInfluencerListSuccess,
                getAssignmentInfluencerListFailed,
                updateProgress,
                params
            );
        }
    }, [props.campaignId, props.campaignDetail?.campaign_data?.type]);

    function getAssignmentInfluencerListSuccess(res, response_data) {
        const influencerData = sanitizeMetrics(response_data?.response || []);
        setAssignmentInfluencerList(influencerData);
        bumpInitialLoadDone();
    }

    function getAssignmentInfluencerListFailed(error) {
        console.error('getAssignmentInfluencerListFailed:', error);
        bumpInitialLoadDone();
    }

    const [timelineLoading, setTimelineLoading] = useState(false);

    const getCampaignAssignmentProgress = useCallback(() => {
        const params = { campaign_id: props.campaignId };
        setTimelineLoading(true);
        Core.getCampaignAssignmentProgress(
            (response) => {
                const payload = response?.response || [];
                if (Array.isArray(payload)) {
                    setTimelineData(sanitizeMetrics(payload));
                } else {
                    setTimelineData([]);
                }
                setTimelineLoading(false);
                bumpInitialLoadDone();
            },
            (err) => {
                console.error('getCampaignTimeline failed:', err);
                setTimelineLoading(false);
                bumpInitialLoadDone();
            },
            () => { }, // Do not use global updateProgress
            params
        );
    }, [props.campaignId]);

    // Callback to update local influencer list state when an influencer is updated
    const handleUpdateInfluencer = useCallback((response) => {
        const updatedAssignments = sanitizeMetrics(response?.response || []);
        if (!Array.isArray(updatedAssignments) || updatedAssignments.length === 0) return;

        setAssignmentInfluencerList(prevList => {
            const newList = prevList.map(item => {
                const updated = updatedAssignments.find(u =>
                    u?.assignment_data?.assignment_id === item?.assignment_data?.assignment_id
                );

                if (updated) {
                    // Deep merge content_analytics to avoid losing other contents
                    const mergedContentAnalytics = {
                        ...(item.content_analytics || {}),
                        ...(updated.content_analytics || {})
                    };

                    return {
                        ...item,
                        ...updated,
                        content_analytics: mergedContentAnalytics
                    };
                }
                return item;
            });

            // Re-calculate all aggregated totals from the entire list to ensure consistency
            let totalViews = 0, totalLikes = 0, totalComments = 0;
            let totalImpressions = 0, totalReach = 0, totalShares = 0, totalSaves = 0, totalRemixes = 0;
            let totalSpentClient = 0, totalSpentUser = 0;

            newList.forEach(item => {
                const analytics = item?.content_analytics || {};
                Object.values(analytics).forEach(content => {
                    totalViews += Number(content.view_count || content.views || 0);
                    totalLikes += Number(content.like_count || 0);
                    totalComments += Number(content.comment_count || 0);
                    totalImpressions += Number(content.impressions || 0);
                    totalReach += Number(content.reach || 0);
                    totalShares += Number(content.share || content.share_count || 0);
                    totalSaves += Number(content.saves || content.save_count || 0);
                    totalRemixes += Number(content.remixes || 0);
                });

                // Financials
                totalSpentClient += Number(item?.assignment_data?.reward_value_client || 0);
                totalSpentUser += Number(item?.reward_data?.value?.amount || 0);
            });

            // Update campaignAnalytic state
            setCampaignContentAnalytic(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    total_play_count: totalViews,
                    total_like_count: totalLikes,
                    total_comment_count: totalComments,
                    views: totalViews,
                    likes: totalLikes,
                    comments: totalComments,
                    impressions: totalImpressions,
                    reach: totalReach,
                    share: totalShares,
                    saves: totalSaves,
                    remixes: totalRemixes,
                };
            });

            // Update campaignReport for Header visuals (Total Spent)
            setCampaignReport(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    total_spent_client: totalSpentClient,
                    total_spent: totalSpentUser,
                    total_spent_client_completed: totalSpentClient,
                    total_spent_completed: totalSpentUser
                };
            });

            return newList;
        });

        // NOTE: We deliberately don't call setRefreshOverview(prev => prev + 1) or getCampaignAssignmentProgress()
        // here to honor the requirement of updating the UI WITHOUT calling any APIs again.
    }, []);

    function AssignmentContentList() {
        const params = {
            brand_id: brandIdResolved,
            campaign_id: props.campaignId,
        };
        Core.AssignmentContentList(
            AssignmentContentListSuccess,
            AssignmentContentListFailed,
            updateProgress,
            params
        );
    }

    function AssignmentContentListSuccess(response) {
        bumpInitialLoadDone();
        const completed = (response?.assignment_list || []).filter((item) => {
            return item?.assignment_data?.assignment_status === "COMPLETED";
        });

        setContentList(completed);

        // Derive platform from completed assignments (fallback to campaign detail if not available)
        try {
            let platformLabel = null;
            if (completed.length > 0) {
                const first = completed[0];

                // Try several possible paths for content_type_id / platform
                let contentTypeId = first?.content_type_id || first?.assignment_data?.content_type_id || first?.content_data?.content_type_id;
                if (!contentTypeId && first?.contents_deep_data) {
                    const keys = Object.keys(first.contents_deep_data || {});
                    if (keys.length > 0) {
                        const content = first.contents_deep_data[keys[0]]?.content_data;
                        contentTypeId = content?.content_type_id;
                    }
                }

                if (contentTypeId) {
                    platformLabel = Utils.socialMediaPlatform(contentTypeId) || null;
                }

                if (!platformLabel) {
                    platformLabel = first?.platforms_data?.[0]?.platform_name || first?.platforms_data?.[0]?.platform_id || first?.platform_id || null;
                }
            }

            // Fallback: try campaign detail content_types
            if (!platformLabel) {
                const ct = props?.campaignDetail?.campaign_data?.content_types;
                if (ct) {
                    try {
                        const parsed = JSON.parse(ct);
                        if (Array.isArray(parsed) && parsed.length > 0) {
                            platformLabel = parsed[0].platform_name || Utils.socialMediaPlatform(parsed[0].content_type_id) || parsed[0].platform_id || null;
                        }
                    } catch (e) { }
                }
            }

            setCampaignPlatform(platformLabel);
        } catch (e) {
            // ignore
        }
    }

    function AssignmentContentListFailed() {
        bumpInitialLoadDone();
    }

    function getCampaignReport() {
        const params = { campaign_id: props.campaignId };
        Core.getCampaignReport(
            getCampaignReportSuccess,
            getCampaignReportFailed,
            updateProgress,
            params
        );
    }

    function getCampaignReportSuccess(response_data) {
        const sanitized = sanitizeMetrics(response_data);
        setCampaignReport(sanitized);
        setInfluencerCount(sanitized?.total_contract || 0);
        setContentCount(sanitized?.total_content_live || 0);
        bumpInitialLoadDone();
    }

    function getCampaignReportFailed() {
        bumpInitialLoadDone();
    }

    function campaignAnalytics() {
        const params = { campaign_id: props.campaignId };
        Core.campaignAnalytics(
            campaignAnalyticsSuccess,
            campaignAnalyticsFailed,
            updateProgress,
            params
        );
    }

    function campaignAnalyticsSuccess(response_data) {
        setCampaignAnalytic(sanitizeMetrics(response_data?.analytics_data));
        bumpInitialLoadDone();
    }

    function campaignAnalyticsFailed() {
        bumpInitialLoadDone();
    }

    function campaignContentAnalytics() {
        const params = {
            org_id: orgIdResolved,
            brand_id: brandIdResolved,
            campaign_id: props.campaignId,
        };
        Core.campaignContentAnalytics(
            campaignContentAnalyticsSuccess,
            campaignContentAnalyticsFailed,
            updateProgress,
            params
        );
    }

    function campaignContentAnalyticsSuccess(response_data) {
        setCampaignContentAnalytic(sanitizeMetrics(response_data?.campaign_content_analytics_data));
        bumpInitialLoadDone();
    }

    function campaignContentAnalyticsFailed() {
        bumpInitialLoadDone();
    }

    // Helpers
    function updateReportType() {
        setReportType(reportType === "client" ? "user" : "client");
    }

    function getReportTotalSpent() {
        if (reportType === "client") {
            return campaignReport?.total_spent_client || 0;
        }
        return campaignReport?.total_spent || 0;
    }

    function openModalShare() {
        setModalIsOpenShare(!modalIsOpenShare);
    }

    function closeShareModal() {
        setModalIsOpenShare(false);
    }

    const openModal = (item) => {
        setCurrentItem(item);
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setCurrentItem(null);
    };

    const handleDownload = (media_id, isVideo, assignment_id) => {
        const mediaUrl = Utils.constructContentMediaUrl(assignment_id, media_id);
        const a = document.createElement("a");
        a.href = mediaUrl;
        a.download = `downloaded_${isVideo ? "video" : "image"}.${media_id.split(".").pop()}`;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    function socialMedialEmbed(item) {
        const platform = Utils.socialMediaPlatform(item?.content_type_id);
        const mediaType = item?.imageSource?.split(".").pop() === "mp4";

        return (
            <div style={{ padding: "20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                    <h3>Content Preview</h3>
                    <PrimaryCta
                        text="Download"
                        iconName="downloadIcon"
                        onClick={() => handleDownload(item?.imageSource, mediaType, item?.assignmentId)}
                    />
                </div>
                {platform === "instagram" ? (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <InstagramEmbed url={item?.videoUrl || item?.url} width={400} />
                    </div>
                ) : platform === "youtube" ? (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <YouTubeEmbed url={item?.videoUrl || item?.url} width={400} height={300} />
                    </div>
                ) : platform === "twitter" ? (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <XEmbed url={item?.videoUrl || item?.url} width={400} />
                    </div>
                ) : null}
            </div>
        );
    }

    // Calculate computed values
    let Engagement =
        Number(campaignContentAnalytic?.total_like_count || 0) +
        Number(campaignContentAnalytic?.total_comment_count || 0);

    let total_play_count = Number(campaignContentAnalytic?.total_play_count || 0);
    let totalEngagement = total_play_count > 0 ? Engagement / total_play_count : 0;
    let avgEngagement = totalEngagement * 100;

    let cpEValue = Engagement > 0 ? getReportTotalSpent() / Engagement : 0;
    let cpmValue = total_play_count > 0 ? getReportTotalSpent() / total_play_count : 0;

    const getStickyOffset = () => {
        const stickyTop = document.querySelector(".analytics-header-top");
        const stickyTabs = document.querySelector(".tabs-sticky-bar");

        const topHeight = stickyTop?.getBoundingClientRect().height || 0;
        const tabsHeight = stickyTabs?.getBoundingClientRect().height || 0;

        // Extra breathing room so heading is not hidden under sticky bars
        return topHeight + tabsHeight + 16;
    };

    // Scroll to section
    const scrollToSection = (tabName) => {
        isManualScrolling.current = true; // Prevent observer from overriding
        setActiveTab(tabName);

        const section = sectionRefs.current[tabName];
        if (section) {
            const targetTop =
                section.getBoundingClientRect().top +
                window.scrollY -
                getStickyOffset();

            window.scrollTo({
                top: Math.max(0, targetTop),
                behavior: "smooth",
            });
        }

        // Re-enable observer after scroll animation completes
        setTimeout(() => {
            isManualScrolling.current = false;
        }, 700);
    };

    // Download PDF
    const downloadPdf = async () => {
        if (!pdfRef.current) return;

        setPDFLoader(true);
        setIsPdfView(true);
        // Small delay to ensure state update propagates to children before capturing
        await new Promise(resolve => setTimeout(resolve, 500));
        try {
            const element = pdfRef.current;
            const originalPadding = element.style.padding;
            const originalBg = element.style.backgroundColor;
            element.style.padding = "20px 30px";
            element.style.backgroundColor = "#FAFAFA";

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
                backgroundColor: '#FAFAFA',
                height: element.scrollHeight,
                windowHeight: element.scrollHeight,
                y: 0,
                scrollY: -window.scrollY,
                onclone: (clonedDoc) => {
                    const clonedElement = clonedDoc.querySelector('.pdf-container');
                    if (clonedElement) {
                        clonedElement.style.height = 'auto';
                        clonedElement.style.overflow = 'visible';
                        // Fix sticky elements in PDF
                        const stickyElements = clonedElement.querySelectorAll('.sticky, .fixed, .analytics-header-top, .tabs-sticky-bar');
                        stickyElements.forEach(el => {
                            el.style.position = 'relative';
                            el.style.top = '0px';
                        });
                    }
                }
            });

            const imgData = canvas.toDataURL("image/png");
            const pdfWidth = 210; // A4 width in mm
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

            // Create PDF with custom format to fit all content
            const pdf = new jsPDF({
                orientation: "p",
                unit: "mm",
                format: [pdfWidth, pdfHeight]
            });
            pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
            pdf.save("campaign-analytics.pdf");

            element.style.padding = originalPadding;
            element.style.backgroundColor = originalBg;
        } catch (error) {
            console.error("PDF generation error", error);
            toast.error("Failed to generate PDF");
        }
        setPDFLoader(false);
        setIsPdfView(false);
    };

    // Process influencer content data
    const influencerContentCards = [];
    // Fetch statistics for Customize Sidebar
    const getCampaignStatistics = useCallback(() => {
        const rId = currentReportId || campaignReport?.report_id;
        if (!rId) return;

        const params = {
            report_id: rId,
            campaign_id: props.campaignId
        };

        Core.getCampaignStatistics(
            (response) => {
                if (response?.response) {
                    setCustomMetricValues(response.response);
                }
            },
            (err) => {
                console.error("Failed to fetch campaign statistics", err);
            },
            () => { }, // No loader needed here as it opens immediately
            params
        );
    }, [currentReportId, campaignReport?.report_id, props.campaignId]);

    useEffect(() => {
        if (customizeMetricsSidebarOpen) {
            getCampaignStatistics();
        }
    }, [customizeMetricsSidebarOpen, getCampaignStatistics]);

    function contentAssignmentInfluencerData() {
        assignmentInfluencerList?.forEach((item) => {
            if (!item?.contents_deep_data) return;
            const contentKeys = Object.keys(item.contents_deep_data);
            if (contentKeys.length === 0) return;
            const contentKey = contentKeys[0];
            const content = item?.contents_deep_data?.[contentKey]?.content_data;
            if (!content) return;

            const influencer = item?.influencer_data;
            const assignmentId = item?.assignment_data?.assignment_id;
            const platform = item?.platforms_data?.[0]?.identifier_id;

            influencerContentCards.push({
                imageSource: content?.media_id,
                url: content?.url,
                content_id: content?.content_id,
                content_type_id: content?.content_type_id,
                identifier_id: item?.platforms_data?.[0]?.identifier_id,
                platform_id: item?.platforms_data?.[0]?.platform_id,
                influencerDp: influencer?.profile_picture,
                influencerId: influencer?.influencer_id,
                influencerUsername: influencer?.name,
                assignmentId: assignmentId,
                influencerHandle: platform,
                campaignId: props.campaignId,
                date: new Date(item?.assignment_data?.date_added * 1000).toLocaleDateString(),
                videoUrl: content?.url,
                type: item?.campaign_data?.type,
            });
        });
    }

    const influencerContentSyncedCards = [];
    function contentAssignmentInfluencerSyncedData() {
        assignmentInfluencerList?.forEach((item) => {
            if (!item) return;
            const assignmentId = item?.assignment_id;
            const platformId = item?.platform_id;

            influencerContentSyncedCards.push({
                url: Utils.generateContentUrl(platformId, item?.content_type_id, item?.shortcode),
                thumbnail: item?.thumbnail,
                influencerUsername: item?.owner_name,
                profile_pic: item?.owner_pic,
                content_id: item?.content_id,
                content_type_id: item?.content_type_id,
                caption: item?.caption,
                identifier_id: item?.identifier_id,
                platform_id: item?.platform_id,
                assignmentId: assignmentId,
                comment_count: item?.comment_count,
                like_count: item?.like_count,
                view_count: item?.views,
                videoUrl: Utils.generateContentUrl(platformId, item?.content_type_id, item?.shortcode),
            });
        });
    }

    // Generate report time
    let formattedTime = null;
    if (reportStatus?.report_generated_at && !isNaN(reportStatus.report_generated_at)) {
        const date = new Date(reportStatus?.report_generated_at * 1000);
        if (!isNaN(date.getTime())) {
            formattedTime = date.toLocaleString("en-GB", {
                day: "numeric",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            });
        }
    }

    // Get content cards
    if (props?.campaignDetail?.campaign_data?.type !== "content") {
        contentAssignmentInfluencerData();
    } else {
        contentAssignmentInfluencerSyncedData();
    }


    // Handle generate report click
    const handleGenerateReport = () => {
        // Step 4: Set IN_PROGRESS state
        setIsGeneratingReport(true);
        setShowFallbackReport(true);
        setReportStatus((prev) => ({
            ...(prev || {}),
            report_status: "IN_PROGRESS",
        }));
        setGenerateReportLoading(true);
        getAssignmentContentReport();
        toast.success("Generating report, please wait...");
    };

    // Handle regenerate report click (same as generate but might already have previous data)
    const handleRegenerateReport = () => {
        // Step 4: Set IN_PROGRESS state
        setIsGeneratingReport(true);
        setReportStatus((prev) => ({
            ...(prev || {}),
            report_status: "IN_PROGRESS",
        }));
        setGenerateReportLoading(true);
        getAssignmentContentReport();
        toast.success("Regenerating report, please wait...");
    };

    const handleOverviewDataLoaded = (data) => {
        if (data?.report_id) {
            setCurrentReportId(data.report_id);
        }

        if (data?.analytics_data) {
            const analyticData = data.analytics_data;
            // Update custom metric values state for sidebar
            // Use full numbers (strings) for editing in sidebar, avoid K/M formatting
            setCustomMetricValues({
                views: Math.round(analyticData.views || analyticData.total_play_count || 0).toString(),
                likes: Math.round(analyticData.likes || analyticData.total_like_count || 0).toString(),
                comments: Math.round(analyticData.comments || analyticData.total_comment_count || 0).toString(),
                er_avg: analyticData.avg_eng_rate !== undefined
                    ? `${Utils.formatEngagementRateDisplay(analyticData.avg_eng_rate)}%`
                    : (campaignReport?.avg_engagement_rate !== undefined
                        ? `${Utils.formatEngagementRateDisplay(campaignReport.avg_engagement_rate)}%`
                        : (avgEngagement ? `${Utils.formatEngagementRateDisplay(avgEngagement)}%` : "0%")),
                er_video: (() => {
                    const views = Number(analyticData.views || analyticData.total_play_count || 0);
                    const reach = Number(analyticData.reach || campaignReport?.total_reach || 0);
                    const val = reach > 0 ? (views / reach) * 10 : 0;
                    return `${Utils.formatEngagementRateDisplay(val)}%`;
                })(),
                er_static: analyticData.static_eng_rate !== undefined ? `${Utils.formatEngagementRateDisplay(analyticData.static_eng_rate)}%` : "-",
                total_engagements: Math.round(analyticData.total_eng_rate || totalEngagement || 0).toString(),

                cpe: analyticData.cpe !== undefined ? (analyticData.cpe < Infinity ? Utils.convertFromINR(analyticData.cpe, user_data?.country) : "₹0") : (cpEValue < Infinity ? Utils.convertFromINR(cpEValue, user_data?.country) : "₹0"),
                cpv: analyticData.cpv !== undefined ? (analyticData.cpv < Infinity ? Utils.convertFromINR(Utils.changeNumberFormate(analyticData.cpv), user_data?.country) : "₹0") : (cpmValue < Infinity ? Utils.convertFromINR(Utils.changeNumberFormate(cpmValue), user_data?.country) : "₹0"),

                impressions: Math.round(analyticData.impressions || 0).toString(),
                reach: Math.round(analyticData.reach || campaignReport?.total_reach || 0).toString(),
                share: Math.round(analyticData.share || 0).toString(),
                saves: Math.round(analyticData.saves || 0).toString(),

                views_subscriber_ratio: analyticData.views_subscriber_ratio || "0",
                profile_engagements: analyticData.profile_eng_rate ? `${Utils.formatEngagementRateDisplay(analyticData.profile_eng_rate)}%` : "0.000%",

                replies: Math.round(analyticData.replies || 0).toString(),
                submissions: Math.round(analyticData.submissions || 0).toString(),
                remixes: Math.round(analyticData.remixes || 0).toString(),
                conversions: Math.round(analyticData.conversions || 0).toString(),
                sticker_taps: Math.round(analyticData.sticker_taps || 0).toString(),

                planned_cpv: analyticData.planned_cpv ? Utils.convertFromINR(analyticData.planned_cpv, user_data?.country) : "₹0",
                planned_views: Math.round(analyticData.planned_views || 0).toString(),
                installs: Math.round(analyticData.installs || 0).toString(),
                registration: Math.round(analyticData.registration || 0).toString(),
                deposit: Math.round(analyticData.deposit || 0).toString(),
                total_clicks: Math.round(analyticData.total_clicks || 0).toString(),
            });
        }
    };

    // Refresh trigger for OverviewSection
    const [refreshOverview, setRefreshOverview] = useState(0);

    // Render - Show "No Contracts" only if we've confirmed there are none
    // Otherwise render page structure immediately (sections handle their own loading states)
    if (hasAssignments === false) {
        return <NoContractsFallback />;
    }

    // If report is in progress (initial generation), show that








    return (
        <div className="analytics-campaign-wrapper">

            <AnalyticsHeaderTop
                orgData={org_deep_data}
                campaignReport={campaignReport}
                reportType={reportType}
                onReportTypeToggle={updateReportType}
                onShareClick={openModalShare}
                onDownloadClick={downloadPdf}
                pdfLoading={pdfLoader}
                isReportGenerated={isReportGenerated}
                campaignDetail={props.campaignDetail}
            />

            <div ref={pdfRef} className="pdf-container" style={{ paddingTop: isPdfView ? 0 : undefined }}>
                {/* Always show RefreshDataBanner to handle "Generate Report" state */}
                {!isPdfView && (
                    <RefreshDataBanner
                        onRefresh={handleRegenerateReport}
                        isLoading={generateReportLoading}
                        lastGenerated={formattedTime}
                        disabled={refreshDisabled}
                        tooltip={refreshDisabled ? `Next report in ${refreshTimeLeft} mins` : null}
                        nextRefreshIn={refreshTimeLeft}
                        isReportGenerated={isReportGenerated}
                    />
                )}

                <AnalyticsHeader
                    campaignDetail={props.campaignDetail}
                    brandData={brand_deep_data?.brand_data}
                    userData={user_data}
                    orgData={org_deep_data}
                    campaignReport={campaignReport}
                    campaignAnalytic={campaignAnalytic}
                    formattedTime={formattedTime}
                    reportType={reportType}
                    onReportTypeToggle={updateReportType}
                    onShareClick={openModalShare}
                    onRegenerateClick={handleRegenerateReport}
                    onDownloadClick={downloadPdf}
                    isReportInProgress={isReportInProgress}
                    pdfLoading={pdfLoader}
                    getReportTotalSpent={getReportTotalSpent}
                    influencerCount={influencerCount}
                    contentCount={contentCount}
                    campaignPlatform={campaignPlatform}
                    isLoading={isLoading}

                    // New Props
                    onGenerateReport={handleGenerateReport} // Always handleGenerateReport because regenerate is now in the banner
                    isReportGenerated={isReportGenerated}
                    refreshDisabled={refreshDisabled}
                    refreshTimeLeft={refreshTimeLeft}
                    lastGeneratedDate={lastGeneratedDate}
                />

                <AnalyticsTabs
                    activeTab={activeTab}
                    onTabClick={scrollToSection}
                    onManageSectionClick={() => setManageSectionSidebarOpen(true)}
                    campaignType={props.campaignDetail?.campaign_data?.type}
                    sectionVisibility={sectionVisibility}
                    visibleTabs={visibleSections}
                    isPdfView={isPdfView}
                />

                <div className="analytics-content">
                    {props.campaignDetail?.campaign_data?.type !== "content" && (
                        <>
                            <OverviewSection
                                sectionRef={(el) => (sectionRefs.current["Overview"] = el)}
                                is_public={false}
                                campaignId={props.campaignId}
                                orgId={org_deep_data?.org_id}
                                brandId={brandIdResolved}
                                userData={user_data}
                                isVisible={sectionVisibility.overview !== false}
                                onCustomizeClick={() => setCustomizeMetricsSidebarOpen(true)}
                                isReportGenerated={isReportGenerated}
                                onDataLoaded={handleOverviewDataLoaded}
                                avgEngagement={props.campaignDetail?.campaign_data?.avg_eng_rate}
                                campaignReport={campaignReport}
                                campaignContentAnalytic={campaignContentAnalytic}
                                refreshTrigger={refreshOverview}
                                campaignPlatform={campaignPlatform}
                                reportType={reportType}
                                isInitialLoading={isLoading}
                                reportStatus={reportStatus?.report_status}
                                influencerList={assignmentInfluencerList}
                            />

                            <TopPerformersSection
                                sectionRef={(el) => (sectionRefs.current["Top Performers"] = el)}
                                is_public={false}
                                campaignId={props.campaignId}
                                isVisible={sectionVisibility.topPerformers !== false}
                                isReportGenerated={isReportGenerated}
                                isInitialLoading={isLoading}
                                reportStatus={reportStatus?.report_status}
                                influencerList={assignmentInfluencerList}
                            />
                        </>
                    )}

                    <TimelineSection
                        sectionRef={(el) => (sectionRefs.current["Timeline"] = el)}
                        campaignDetail={props.campaignDetail}
                        is_public={false}
                        campaignId={props.campaignId}
                        contentData={assignmentInfluencerList}
                        isReportGenerated={isReportGenerated}
                        isVisible={sectionVisibility.timeline !== false}
                        isLoading={isLoading}
                        reportStatus={reportStatus?.report_status}
                        refreshTrigger={refreshOverview}
                    />

                    <InfluencersSection
                        sectionRef={(el) => {
                            sectionRefs.current["Influencers"] = el;
                        }}
                        campaignId={props.campaignId}
                        openModal={openModal}
                        is_public={false}
                        shareKey={undefined}
                        isVisible={sectionVisibility.influencers !== false}
                        isReportGenerated={isReportGenerated}
                        campaignType={props.campaignDetail?.campaign_data?.type}
                        isInitialLoading={isLoading}
                        reportStatus={reportStatus?.report_status}
                        influencerList={assignmentInfluencerList}
                        onUpdateInfluencer={handleUpdateInfluencer}
                        isPdfView={isPdfView}
                    />

                    <ContentSection
                        sectionRef={(el) => (sectionRefs.current["Content"] = el)}
                        campaignId={props.campaignId}
                        campaignDetail={props.campaignDetail}
                        is_public={false}
                        shareKey={undefined}
                        isReportGenerated={isReportGenerated}
                        isVisible={sectionVisibility.content !== false}
                        influencerList={assignmentInfluencerList}
                        isInitialLoading={isLoading}
                        reportStatus={reportStatus?.report_status}
                    />
                </div>
            </div>

            {/* Modals */}
            {modalIsOpenShare && (
                <InfluencerAnalyticsShareLink
                    modalIsOpen={modalIsOpenShare}
                    closeModal={closeShareModal}
                    campaignId={props.campaignId}
                    assignment_share_key={props.campaignDetail?.campaign_data?.assignment_share_key}
                    sectionVisibility={sectionVisibility}
                />
            )}

            <Modal
                style={{
                    content: {
                        top: "50%",
                        left: "50%",
                        right: "auto",
                        bottom: "auto",
                        marginRight: "-50%",
                        transform: "translate(-50%, -50%)",
                        background: "var(--general-secondary-color)",
                        textAlign: "center",
                        zIndex: "5",
                        height: "40%",
                        display: "flex",
                        flexDirection: "column",
                        minWidth: "300px",
                        minHeight: "500px",
                    },
                    overlay: {
                        background: "rgba(0, 0, 0, 0.95)",
                        zIndex: 10,
                    },
                }}
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
            >
                <div onClick={closeModal} className="close-btn-icon">
                    <Icon iconName="CloseRoundButtonIcon" />
                </div>
                {currentItem && socialMedialEmbed(currentItem)}
            </Modal>

            {/* Manage Section Sidebar */}
            <ManageSectionSidebar
                isOpen={manageSectionSidebarOpen}
                onClose={() => setManageSectionSidebarOpen(false)}
                sectionVisibility={sectionVisibility}
                isLoading={manageSectionSaving}
                campaignType={props.campaignDetail?.campaign_data?.type}
                onSaveVisibility={(visibility) => {
                    const params = {
                        campaign_id: props.campaignId,
                        visibility: visibility,
                    };

                    Core.reportSectionSetting(
                        () => {
                            // Parse the visibility string back to object before updating state
                            const visibilityObject = typeof visibility === 'string'
                                ? JSON.parse(visibility)
                                : visibility;
                            setSectionVisibility(visibilityObject);
                            setManageSectionSidebarOpen(false);
                            toast.success("Visibility settings saved successfully");
                        },
                        (err) => {
                            console.error('reportSectionSetting failed:', err);
                            toast.error("Failed to save visibility settings");
                        },
                        (loading) => {
                            setManageSectionSaving(loading);
                        },
                        params
                    );
                }}
            />

            {/* Customize Metrics Sidebar */}
            <CustomizeMetricsSidebar
                isOpen={customizeMetricsSidebarOpen}
                onClose={() => setCustomizeMetricsSidebarOpen(false)}
                currentMetricValues={customMetricValues}
                reportId={currentReportId || campaignReport?.report_id}
                campaignId={props.campaignId}
                onSaveCustomValues={() => {
                    setRefreshOverview(prev => prev + 1);
                    setCustomizeMetricsSidebarOpen(false);
                }}
            />
        </div>
    );
}

export default AnalyticsCampaign;
